import { useCallback, useEffect, useRef, useState } from 'react'
import { Button, Icon } from 'semantic-ui-react'
import { useTranslation } from 'react-i18next'
import { usePricerOrder } from '../../services/PricerOrderProvider'
import Barcoder from 'barcoder'
import './PricerBlinkButton.css'

const PricerBlinkButton = ({ orderItem }) => {
  const [t] = useTranslation()
  const blinkTimeoutRef = useRef(null)
  const { flash, isBlinking: isBlinkingContext, isActive } = usePricerOrder()

  const isBlinking = isBlinkingContext(orderItem.object?.barcode)

  // Local override: true while the 30 s client-side timer is running
  const [isBlinkingLocal, setIsBlinkingLocal] = useState(false)

  const stopBlink = useCallback(() => {
    if (blinkTimeoutRef.current) {
      clearTimeout(blinkTimeoutRef.current)
      blinkTimeoutRef.current = null
    }
    setIsBlinkingLocal(false)
  }, [])

  const startBlink = useCallback(() => {
    stopBlink()
    setIsBlinkingLocal(true)
    blinkTimeoutRef.current = setTimeout(stopBlink, 30000)
  }, [stopBlink])

  // Sync with context (e.g. another tab started / stopped the blink)
  useEffect(() => {
    if (isBlinking) {
      startBlink()
    } else {
      stopBlink()
    }
  }, [isBlinking, startBlink, stopBlink])

  // Cleanup on unmount
  useEffect(() => stopBlink, [stopBlink])

  const handleClick = useCallback(() => {
    flash([orderItem.object.barcode])
    startBlink()
  }, [flash, orderItem.object?.barcode, startBlink])

  const hasValidEan = orderItem.object?.barcode && Barcoder.validate(orderItem.object.barcode)

  const isAnimating = isBlinking || isBlinkingLocal

  if (!isActive || !hasValidEan) return null

  return (
    <Button
      icon
      color="grey"
      labelPosition="right"
      onClick={handleClick}
      title={t('pricer.blink_button')}
      aria-busy={isAnimating}
      className={isAnimating ? 'pricer-blink-icon' : undefined}
    >
      {isAnimating ? t('pricer.blink_button_active') : t('pricer.blink_button_idle')}
      <Icon name="wifi" />
    </Button>
  )
}

export { PricerBlinkButton }
